import { aircraftDetailQuery } from "@common/api/aircraft"
import { atlPageQuery } from "@common/api/atl"
import type { AtlPageDetailPublic } from "@common/client"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { PageForm } from "@/routes/_layout/aircraft_/$aircraftId/tech-log/-components/form/page-form"
import { PagePane } from "@/routes/_layout/aircraft_/$aircraftId/tech-log/-components/page-pane"
import { CLOSED_SCAN } from "@/routes/_layout/aircraft_/$aircraftId/tech-log/-components/source"

export const Route = createFileRoute("/_layout/tech-logs/$pageId")({
  loader: async ({ context: { queryClient }, params: { pageId } }) => {
    const page = await queryClient.ensureQueryData(
      atlPageQuery({ atl_page_id: pageId }),
    )
    if (page.aircraft_id) {
      await queryClient.ensureQueryData(
        aircraftDetailQuery({ id: page.aircraft_id }),
      )
    }
  },
  component: TechLogPagePane,
})

function usePaneActions(page: AtlPageDetailPublic): {
  close: () => void
  remove: (() => void) | undefined
} {
  const canDelete = useHasPermission("atl_page:delete")
  const navigate = useNavigate({ from: Route.fullPath })
  return {
    close: () =>
      navigate({
        to: "/tech-logs",
        search: (previous) => ({
          ...previous,
          ...CLOSED_SCAN,
          log_tab: undefined,
          wide: undefined,
        }),
      }),
    remove: canDelete
      ? () =>
          navigate({
            to: "/tech-logs/delete/$pageId",
            params: { pageId: page.id },
            search: true,
          })
      : undefined,
  }
}

function TechLogPagePane(): React.ReactElement {
  const { pageId } = Route.useParams()
  const { data: page } = useSuspenseQuery(atlPageQuery({ atl_page_id: pageId }))

  return page.status === "recorded" && page.aircraft_id ? (
    <RecordedPane key={page.id} page={page} aircraftId={page.aircraft_id} />
  ) : (
    <DraftPane key={page.id} page={page} />
  )
}

function DraftPane({
  page,
}: {
  page: AtlPageDetailPublic
}): React.ReactElement {
  const { close, remove } = usePaneActions(page)
  const showToast = useCustomToast()

  return (
    <PagePane
      title={page.log_number ? `${page.log_number} · Draft` : "Draft"}
      page={page}
      stepper={undefined}
      onClose={close}
    >
      <PageForm
        key={page.id}
        subject={{
          kind: "draft",
          page,
          onRecorded: (recorded) =>
            showToast(
              `Recorded ${recorded.log_number}`,
              recorded.aircraft_registration ?? "",
              "success",
            ),
        }}
        onVoid={remove}
      />
    </PagePane>
  )
}

function RecordedPane({
  page,
  aircraftId,
}: {
  page: AtlPageDetailPublic
  aircraftId: string
}): React.ReactElement {
  const { data: aircraft } = useSuspenseQuery(
    aircraftDetailQuery({ id: aircraftId }),
  )
  const { close, remove } = usePaneActions(page)

  return (
    <PagePane
      title={page.log_number ?? "Draft"}
      page={page}
      stepper={undefined}
      onClose={close}
    >
      <PageForm
        key={page.id}
        subject={{ kind: "recorded", aircraft, page }}
        onVoid={remove}
      />
    </PagePane>
  )
}
